import dopo1 from "@/assets/dopo1.webp.asset.json";
import { EMAIL, ANNO_FONDAZIONE, GOOGLE_MAPS_URL, TEL_ANTONIO, TEL_LUCA } from "@/lib/site";
import { FACEBOOK_URL, INSTAGRAM_URL } from "@/components/Social";
import type { PaginaProvincia } from "@/lib/province-pagine";

const BASE = "https://artigianidelcotto.com";

/** NAP condiviso (Name, Address, Phone) — deve restare identico su tutte le pagine. */
const NOME_AZIENDA = "Artigiani del Cotto";
const INDIRIZZO = {
  "@type": "PostalAddress",
  streetAddress: "Torbole Casaglia",
  addressLocality: "Torbole Casaglia",
  addressRegion: "BS",
  postalCode: "25030",
  addressCountry: "IT",
} as const;

export function headProvincia(dati: PaginaProvincia) {
  const url = `${BASE}/${dati.slug}`;
  const titolo =
    dati.titoloSeo ?? `Pavimenti in cotto ${dati.citta}: lavaggio e trattamento`;
  const descrizione =
    dati.descrizioneSeo ??
    `Lavaggio, deceratura e trattamento protettivo di pavimenti in cotto a ${dati.citta} e provincia. Sopralluogo e preventivo gratuiti: chiama 339 751 7813.`;


  const immagine = `${BASE}${dopo1.url}`;
  const comuniServiti = (dati.comuni ?? []).map((c) => ({
    "@type": "City",
    name: c,
  }));


  return {
    meta: [
      { title: titolo },
      { name: "description", content: descrizione },
      { property: "og:title", content: titolo },
      { property: "og:description", content: descrizione },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:image", content: immagine },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: immagine },
      { name: "geo.region", content: dati.geo },
      { name: "geo.placename", content: dati.citta },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${BASE}/#organizzazione`,
              name: NOME_AZIENDA,
              legalName: NOME_AZIENDA,
              url: `${BASE}/`,
              description:
                "Artigiani specializzati nel lavaggio e nel trattamento protettivo di pavimenti in cotto, marmi, pietre, graniglie e grès in tutta la Lombardia.",
              foundingDate: String(ANNO_FONDAZIONE),
              image: immagine,
              logo: immagine,
              email: EMAIL,
              telephone: TEL_ANTONIO,
              address: INDIRIZZO,
              sameAs: [FACEBOOK_URL, INSTAGRAM_URL, GOOGLE_MAPS_URL],
              areaServed: { "@type": "AdministrativeArea", name: "Lombardia" },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "customer service",
                  name: "Antonio",
                  telephone: TEL_ANTONIO,
                  email: EMAIL,
                  areaServed: "IT",
                  availableLanguage: ["it"],
                },
                {
                  "@type": "ContactPoint",
                  contactType: "sales",
                  name: "Luca",
                  telephone: TEL_LUCA,
                  areaServed: "IT",
                  availableLanguage: ["it"],
                },
              ],
            },
            {
              "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
              "@id": `${url}#azienda`,
              name: `${NOME_AZIENDA} — ${dati.citta}`,
              alternateName: NOME_AZIENDA,
              description: descrizione,
              url,
              telephone: TEL_ANTONIO,
              email: EMAIL,
              image: immagine,
              logo: immagine,
              foundingDate: String(ANNO_FONDAZIONE),
              priceRange: "€€",
              currenciesAccepted: "EUR",
              paymentAccepted: "Contanti, Bonifico bancario",
              sameAs: [FACEBOOK_URL, INSTAGRAM_URL, GOOGLE_MAPS_URL],
              hasMap: GOOGLE_MAPS_URL,
              parentOrganization: { "@id": `${BASE}/#organizzazione` },
              address: INDIRIZZO,
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "customer service",
                  name: "Antonio",
                  telephone: TEL_ANTONIO,
                  email: EMAIL,
                  areaServed: dati.sigla,
                  availableLanguage: ["it"],
                },
                {
                  "@type": "ContactPoint",
                  contactType: "sales",
                  name: "Luca",
                  telephone: TEL_LUCA,
                  areaServed: dati.sigla,
                  availableLanguage: ["it"],
                },
              ],
              geo: {
                "@type": "GeoCoordinates",
                latitude: dati.lat,
                longitude: dati.lon,
              },
              areaServed: [
                {
                  "@type": "GeoCircle",
                  name: `${dati.citta} e provincia`,
                  geoMidpoint: {
                    "@type": "GeoCoordinates",
                    latitude: dati.lat,
                    longitude: dati.lon,
                  },
                  geoRadius: String(dati.raggio),
                },
                { "@type": "City", name: dati.citta },
                {
                  "@type": "AdministrativeArea",
                  name: `Provincia di ${dati.citta} (${dati.sigla})`,
                },
                ...comuniServiti,
              ],
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                  ],
                  opens: "08:00",
                  closes: "19:00",
                },
              ],
              makesOffer: [
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: `Lavaggio e trattamento pavimenti in cotto a ${dati.citta}`,
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: `Lavaggio e trattamento protettivo di marmi, pietre e graniglie a ${dati.citta}`,
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: `Lavaggio grès e cemento a ${dati.citta}`,
                  },
                },
              ],
              knowsAbout: [
                "lavaggio e trattamento pavimenti in cotto",
                "lavaggio e trattamento protettivo di marmi e pietre",
                "lavaggio e trattamento di graniglie e seminati",
                "deceratura e ceratura pavimenti",
              ],
            },
            {
              "@type": "Service",
              "@id": `${url}#servizio`,
              name: `Lavaggio e trattamento pavimenti in cotto a ${dati.citta}`,
              serviceType: "Lavaggio e trattamento protettivo pavimenti",

              description: descrizione,
              url,
              provider: { "@id": `${url}#azienda` },
              areaServed: [
                { "@type": "City", name: dati.citta },
                { "@type": "AdministrativeArea", name: `Provincia di ${dati.citta}` },
                ...comuniServiti,
              ],

              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: `Servizi a ${dati.citta} e provincia`,
                itemListElement: [
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Lavaggio e trattamento pavimenti in cotto",
                      description:
                        "Lavaggio profondo, rimozione di cere vecchie e trattamento protettivo o a cera, in interno e in esterno.",
                    },
                  },
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Lavaggio e trattamento protettivo di marmi, pietre e graniglie",
                      description:
                        "Solo lavaggio e trattamento protettivo di pavimenti, scale e davanzali. Non eseguiamo levigatura, lucidatura a piombo o cristallizzazione.",
                    },
                  },
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Lavaggio grès e cemento",
                      description:
                        "Rimozione di aloni, velo di posa e sporco incrostato, con protezione antimacchia dove serve.",
                    },
                  },
                ],
              },
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                url: `${url}#contatti`,
                description: "Sopralluogo e preventivo gratuiti.",
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: dati.faq.map((f) => ({
                "@type": "Question",
                name: f.domanda,
                acceptedAnswer: { "@type": "Answer", text: f.risposta },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${url}#briciole`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
                { "@type": "ListItem", position: 2, name: dati.citta, item: url },
              ],
            },

          ],
        }),
      },
    ],
  };
}
